import { useCallback, useEffect, useRef } from 'react'

/**
 * Throttles a callback to at most once per `ms` -- shared by the map's
 * and timeline's hover-sync handlers (both call `setHoveredTimestampMs`
 * on every `mousemove`/axis-pointer event, which is far more often than
 * the store/UI needs to react). Originally written inline in
 * `activity-map.tsx`; extracted here (M8 follow-up) so
 * `activity-timeline.tsx` reuses the exact same throttling behavior
 * instead of a second, separately-maintained implementation.
 *
 * Always calls the *latest* `fn` (via a ref updated in an effect, not
 * captured at throttle-setup time) so callers don't need to worry about
 * stale closures when `fn` changes across renders -- only `ms` is a real
 * dependency of the returned callback's identity.
 */
export function useThrottled<Args extends unknown[]>(
  fn: (...args: Args) => void,
  ms: number,
): (...args: Args) => void {
  const lastRef = useRef(0)
  const fnRef = useRef(fn)
  useEffect(() => {
    fnRef.current = fn
  }, [fn])
  return useCallback(
    (...args: Args) => {
      const now = Date.now()
      if (now - lastRef.current < ms) return
      lastRef.current = now
      fnRef.current(...args)
    },
    [ms],
  )
}
